import React, { useEffect, useState } from 'react';
import { Stethoscope, Scale, User, ArrowRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Demo-only welcome overlay: pick a persona and get signed straight into
// that view. Renders nothing outside the demo build (REACT_APP_DEMO) or
// once a session exists / it was skipped this browser session.
// Credentials are the public demo logins from database/demo/README.md —
// they only exist in the demo bundle and demo Supabase project.

const DEMO_PASSWORD = 'MediLinkDemo2026!';

const ROLES = [
  {
    key: 'provider',
    label: 'Medical Provider',
    persona: 'Dr. Maya Reyes — Summit Spine & Rehab',
    blurb: 'Scheduling, patient profiles, referrals and billing for a two-location orthopedic practice.',
    email: 'demo-provider@medilink.vip',
    dest: '/app/dashboard',
    Icon: Stethoscope,
  },
  {
    key: 'lawfirm',
    label: 'Law Firm',
    persona: 'Alex Harper — Harbor & Stone Injury Law',
    blurb: 'Track clients through treatment, see appointments as they happen, share documents with providers.',
    email: 'demo-attorney@medilink.vip',
    dest: '/app/dashboard',
    Icon: Scale,
  },
  {
    key: 'patient',
    label: 'Patient',
    persona: 'Jordan Smith — auto-accident case',
    blurb: 'Appointments, forms to sign and the provider directory, the way a client sees them.',
    email: 'demo-patient@medilink.vip',
    dest: '/client',
    Icon: User,
  },
];

export default function DemoWelcome() {
  const isDemo = process.env.REACT_APP_DEMO === 'true';
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(null);   // role key while signing in
  const [error, setError] = useState('');

  // Tokenized public pages (a patient's booking link, a guest video visit,
  // an invite) are opened by outsiders — never cover them with the picker.
  const publicPage = /^\/(book|visit|client-invite|invite)\//.test(window.location.pathname);
  useEffect(() => {
    if (!isDemo || publicPage) return;
    if (!sessionStorage.getItem('demo-welcome-done')) {
      supabase.auth.getSession().then(({ data }) => {
        if (!data?.session) setVisible(true);
      });
    }
    // Signing out returns you to the welcome screen, not the bare login page
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        sessionStorage.removeItem('demo-welcome-done');
        setBusy(null);
        setVisible(true);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [isDemo, publicPage]);

  if (!isDemo || publicPage || !visible) return null;

  const enter = async (role) => {
    setBusy(role.key); setError('');
    const { error: err } = await supabase.auth.signInWithPassword({
      email: role.email,
      password: DEMO_PASSWORD,
    });
    if (err) { setError(err.message); setBusy(null); return; }
    import('../../lib/api').then(({ api }) => api.recordLogin()).catch(() => {});
    sessionStorage.setItem('demo-welcome-done', '1');
    window.location.href = role.dest;
  };

  const skip = () => {
    sessionStorage.setItem('demo-welcome-done', '1');
    setVisible(false);
  };

  return (
    <div className="demo-welcome" style={{
      zIndex: 2147483000,
      background: 'radial-gradient(1200px 700px at 70% -10%, rgba(13,167,202,0.25), transparent 60%), linear-gradient(160deg, #101733 0%, #22346A 55%, #0f2547 100%)',
    }}>
      <div className="demo-welcome-inner">
        <img
          src="/demo-mascot.png"
          alt="MediLink demo guide"
          className="demo-welcome-mascot"
          style={{ filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.35))' }}
        />
        <h1>
          Welcome to the MediLink Demo
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 15, margin: '0 auto 28px', maxWidth: 520 }}>
          Sample data only — it resets every night. Which view would you like to explore?
        </p>

        <div className="demo-welcome-grid">
          {ROLES.map(role => (
            <button
              key={role.key}
              onClick={() => enter(role)}
              disabled={busy !== null}
              className="demo-welcome-card"
              style={{
                textAlign: 'left', cursor: busy ? 'wait' : 'pointer',
                background: 'rgba(255,255,255,0.97)', border: 'none', borderRadius: 16,
                padding: '22px 20px', boxShadow: '0 18px 40px -18px rgba(0,0,0,0.5)',
                transition: 'transform .15s ease', opacity: busy && busy !== role.key ? 0.6 : 1,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
            >
              <div style={{
                width: 42, height: 42, borderRadius: 12, marginBottom: 12,
                background: 'linear-gradient(135deg, #0DA7CA, #22346A)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
              }}>
                <role.Icon size={20} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#101733', marginBottom: 3 }}>
                {busy === role.key ? 'Signing you in…' : role.label}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#0DA7CA', marginBottom: 8 }}>{role.persona}</div>
              <div className="demo-welcome-blurb" style={{ fontSize: 12.5, lineHeight: 1.5, color: '#4b5563' }}>{role.blurb}</div>
              <div style={{
                marginTop: 12, fontSize: 12.5, fontWeight: 700, color: '#22346A',
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}>
                Explore this view <ArrowRight size={14} />
              </div>
            </button>
          ))}
        </div>

        {error && (
          <div style={{ marginTop: 16, color: '#fecaca', fontSize: 13 }}>{error}</div>
        )}

        <button
          onClick={skip}
          style={{
            marginTop: 26, background: 'none', border: 'none', cursor: 'pointer',
            color: 'rgba(255,255,255,0.65)', fontSize: 13, textDecoration: 'underline',
          }}
        >
          Skip — I'll sign in myself
        </button>
      </div>
    </div>
  );
}
